import { ApiPathStatus, ApiSimpleComponent } from "@incendium/api";
import { Button, Grid, Portal, Typography } from "@mui/material";
import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import { useNotification, useSelectedProject } from "Hooks";
import { LightningLanderCard } from "features/lightningLanders";
import { LanderEditOptions } from "features/lightningLanders/types";
import produce from "immer";
import { Suspense, useCallback } from "react";
import { useConfirmation } from "Hooks/useConfirmation";
import { landersService } from "Apis";
import { useHistory } from "react-router-dom";
import { useLanderComponents } from "features/lightningLanders/hooks/useLanderComponents";
import { deleteComponent } from "features/campaigns";
import { usePollPendingComponents } from "features/campaigns";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";

function LightningLandersPage() {
  const { selectedProject } = useSelectedProject();
  const { components, setComponents, loading, refetch } = useLanderComponents();
  const handleDelete = useConfirmation();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const history = useHistory();
  const { pathByName } = useProtectedRoutes();

  const newLandingPath = useCallback(() => {
    history.push(`./lightning-landing-pages/new`);
  }, [history]);

  const editLandingPath = useCallback(
    (component: ApiSimpleComponent, options?: LanderEditOptions) => {
      const path = pathByName(RouteNames.LIGHTNING_LANDING_PAGE, {
        clientId: selectedProject?.clientId,
        projectId: selectedProject?.id,
        componentId: component.id,
      });
      history.push(
        options?.control ? `${path}?control=${options.control}` : path,
      );
    },
    [history, pathByName, selectedProject?.clientId, selectedProject?.id],
  );

  const deleteLandingPath = useCallback(
    (component: ApiSimpleComponent) => {
      handleDelete({
        title: "Are you sure you want to delete this Lander",
        body: `Deleting the Lander with path ${
          component.path?.path || component.name
        } will mean this page will not be reachable anymore, Are you happy to continue`,
        callback: async () => {
          if (component.path) {
            await landersService.landersServiceDeleteLander({
              projectId: selectedProject?.id as number,
              landerId: component.path.id as number,
            });
          } else {
            await deleteComponent(selectedProject?.id, component.id);
          }

          setComponents((components) =>
            produce(components, (draft) => {
              const idx = draft.findIndex((d) => d.id === component.id);
              if (idx >= 0 && draft[idx].path) {
                draft[idx].path!.status = ApiPathStatus.PATH_STATUS_DELETING;
              } else if (idx >= 0) {
                draft.splice(idx, 1);
              }
            }),
          );

          return `Lander ${
            component.path?.path || component.name
          } set to delete, this may take awhile, please check back later`;
        },
      });
    },
    [handleDelete, selectedProject, setComponents],
  );

  const onActivateSwitch = useCallback(
    (activate: boolean) => async (lander) => {
      try {
        const fn = activate
          ? landersService.landersServiceActivateLander({
              projectId: selectedProject?.id as number,
              landerId: lander.id as number,
            })
          : landersService.landersServiceDeactivateLander({
              projectId: selectedProject?.id as number,
              landerId: lander.id as number,
            });
        await fn;

        showSuccessNotification(
          `${lander.path} ${activate ? "Activated" : "Deactivated"}`,
        );
        // due to a few chnges and ednpojt not returning full lander, we just refetch them all
        refetch();
      } catch (error) {
        showErrorNotification("Internal error, please try again");
      }
    },
    [selectedProject, showErrorNotification, showSuccessNotification, refetch],
  );

  // Poll for components with pending AI prompts
  usePollPendingComponents({
    projectId: selectedProject?.id,
    components: components,
    setComponents: setComponents,
  });

  return (
    <>
      <Portal container={() => document.getElementById("pageAction")}>
        <Button onClick={newLandingPath}>New Landing Page</Button>
      </Portal>
      <Suspense fallback={<Loading />}>
        {!loading && components.length === 0 ? (
          <CenterPage calcHeight>
            <Typography variant="subtitle1">
              You don't have any Lightning Landers yet.
            </Typography>
            <Typography variant="subtitle1" mb={2} color={"secondary"}>
              Click the button below to get started!
            </Typography>
            <Button onClick={newLandingPath}>New Landing Page</Button>
          </CenterPage>
        ) : (
          <Grid container spacing={2} pb={4}>
            {components.map((lp) => (
              <Grid key={lp.id} item xs={4}>
                <LightningLanderCard
                  lander={lp.path}
                  component={lp}
                  project={selectedProject!}
                  onEdit={editLandingPath}
                  onDelete={deleteLandingPath}
                  onActivate={onActivateSwitch(true)}
                  onDeactivate={onActivateSwitch(false)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Suspense>
    </>
  );
}

export default LightningLandersPage;
